import React, { useState, useEffect } from 'react';
import { 
  Card, CardContent, Typography, TextField, Button, Table, TableBody, 
  TableCell, TableContainer, TableHead, TableRow, Paper, Grid, Box,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Chip
} from '@mui/material';
import { ArrowUpward, ArrowDownward, Add, Close, People } from '@mui/icons-material';

import { useApiOperations } from '../hooks/useApiOperations';
import { ActionButtons } from './shared/ActionButtons';
import { useAuth } from '../contexts/AuthContext';
import ManufacturerGotoPersons from './ManufacturerGotoPersons';
import { apiGet } from '../utils/apiHelpers';

function Manufacturers() {
  const user = useAuth();
  const canEdit = user && ['admin', 'account_onboarding'].includes(user.role);
  const [manufacturers, setManufacturers] = useState([]);
  const [newManufacturer, setNewManufacturer] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState(null); // null, 'asc', 'desc'
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [contactPersonsDialogOpen, setContactPersonsDialogOpen] = useState(false);
  const [selectedManufacturer, setSelectedManufacturer] = useState(null);
  const [contactPersonsCounts, setContactPersonsCounts] = useState({});
  
  const { fetchData, createItem, updateItem, deleteItem } = useApiOperations('/api/manufacturers');

  useEffect(() => {
    loadManufacturers();
  }, []);

  const loadManufacturers = async () => {
    try {
      const data = await fetchData();
      setManufacturers(data);
      loadContactPersonsCounts();
    } catch (error) {
      console.error('Error fetching manufacturers:', error);
    }
  };

  const loadContactPersonsCounts = async () => {
    try {
      const response = await apiGet('/api/manufacturers/contact-counts');
      setContactPersonsCounts(response.data || {});
    } catch (error) {
      console.error('Error fetching points of contact counts:', error);
      // Set empty object to prevent UI errors
      setContactPersonsCounts({});
    }
  };

  const openContactPersonsDialog = (manufacturer) => {
    setSelectedManufacturer(manufacturer);
    setContactPersonsDialogOpen(true);
  };

  const closeContactPersonsDialog = (shouldRefresh = false) => {
    setContactPersonsDialogOpen(false);
    setSelectedManufacturer(null);
    // Reload counts immediately if needed
    if (shouldRefresh) {
      loadContactPersonsCounts();
    }
  };

  const addManufacturer = async (e) => {
    e.preventDefault();
    try {
      await createItem({ manufacturer_name: newManufacturer });
      setNewManufacturer('');
      setAddDialogOpen(false);
      loadManufacturers();
    } catch (error) {
      console.error('Error adding manufacturer:', error);
    }
  };

  const updateManufacturer = async (id, newName) => {
    try {
      await updateItem(id, { manufacturer_name: newName });
      setEditingId(null);
      loadManufacturers();
    } catch (error) {
      console.error('Error updating manufacturer:', error);
    }
  };

  const deleteManufacturer = async (id) => {
    if (window.confirm('Are you sure?')) {
      try {
        await deleteItem(id);
        loadManufacturers();
      } catch (error) {
        console.error('Error deleting manufacturer:', error);
      }
    }
  };

  const startEdit = (manufacturer) => {
    setEditingId(manufacturer.manufacturer_id);
    setEditValue(manufacturer.manufacturer_name);
  };

  const handleSort = () => {
    if (sortOrder === null || sortOrder === 'desc') {
      setSortOrder('asc');
    } else {
      setSortOrder('desc');
    }
  };

  const getSortedManufacturers = (filteredManufacturers) => {
    if (sortOrder === null) return filteredManufacturers;
    
    return [...filteredManufacturers].sort((a, b) => {
      const aName = a.manufacturer_name.toLowerCase();
      const bName = b.manufacturer_name.toLowerCase();
      
      if (sortOrder === 'asc') {
        return aName.localeCompare(bName);
      } else {
        return bName.localeCompare(aName);
      }
    });
  };

  return (
    <Box>
      <Grid container spacing={3}>
        <Grid size={12}>
          <Card elevation={3} sx={{ borderRadius: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
                  Evoke Medical Care Manufacturers
                </Typography>
                {canEdit && (
                  <Button
                    variant="outlined"
                    startIcon={<Add />}
                    onClick={() => setAddDialogOpen(true)}
                    sx={{ 
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 3
                    }}
                  >
                    Add Manufacturer
                  </Button>
                )}
              </Box>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid
                  size={{
                    xs: 12,
                    md: 6,
                    lg: 4
                  }}>
                  <TextField
                    fullWidth
                    label="Search Manufacturers"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    size="small"
                    sx={{ 
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2
                      }
                    }}
                  />
                </Grid>
              </Grid>

              <TableContainer component={Paper} sx={{ 
                maxHeight: 600, 
                overflow: 'auto',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider'
              }}>
                <Table stickyHeader size="medium">
                  <TableHead>
                    <TableRow sx={{ '& .MuiTableCell-head': { backgroundColor: 'grey.50', fontWeight: 600 } }}>
                      <TableCell sx={{ cursor: 'pointer', userSelect: 'none', width: canEdit ? '50%' : '60%' }} onClick={handleSort}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          Manufacturer Name
                          {sortOrder === 'asc' && <ArrowUpward sx={{ fontSize: 18, color: 'primary.main' }} />}
                          {sortOrder === 'desc' && <ArrowDownward sx={{ fontSize: 18, color: 'primary.main' }} />}
                          {sortOrder === null && <ArrowUpward sx={{ fontSize: 18, color: 'grey.400' }} />}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ width: canEdit ? '30%' : '40%' }}>Points of Contact</TableCell>
                      {canEdit && <TableCell sx={{ width: '20%' }}>Actions</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {getSortedManufacturers(
                      manufacturers.filter(manufacturer => 
                        manufacturer.manufacturer_name.toLowerCase().includes(searchTerm.toLowerCase())
                      )
                    )
                      .map((manufacturer) => (
                      <TableRow key={manufacturer.manufacturer_id} sx={{ '&:hover': { backgroundColor: 'grey.50' } }}>
                        <TableCell>
                          {canEdit && editingId === manufacturer.manufacturer_id ? (
                            <TextField
                              size="small"
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              variant="outlined"
                              sx={{ minWidth: 120 }}
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  updateManufacturer(manufacturer.manufacturer_id, editValue);
                                }
                              }}
                            />
                          ) : (
                            manufacturer.manufacturer_name
                          )}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip 
                              label={`${contactPersonsCounts[manufacturer.manufacturer_id] || 0} persons`}
                              size="small"
                              color={contactPersonsCounts[manufacturer.manufacturer_id] > 0 ? 'primary' : 'default'}
                            />
                            <Button
                              size="small"
                              startIcon={<People />}
                              onClick={() => openContactPersonsDialog(manufacturer)}
                              sx={{ textTransform: 'none' }}
                            >
                              {canEdit ? 'Manage' : 'View'}
                            </Button>
                          </Box>
                        </TableCell>
                        {canEdit && (
                          <TableCell>
                            <ActionButtons
                              isEditing={editingId === manufacturer.manufacturer_id}
                              onEdit={() => startEdit(manufacturer)}
                              onSave={() => updateManufacturer(manufacturer.manufacturer_id, editValue)}
                              onCancel={() => setEditingId(null)}
                              onDelete={() => deleteManufacturer(manufacturer.manufacturer_id)}
                            />
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Add Manufacturer Dialog */}
      <Dialog 
        open={addDialogOpen} 
        onClose={() => setAddDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        slotProps={{
          paper: {
            sx: { borderRadius: 3 }
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Add New Manufacturer
          </Typography>
          <IconButton
            onClick={() => setAddDialogOpen(false)}
            size="small"
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <form onSubmit={addManufacturer}>
          <DialogContent sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Manufacturer Name"
              value={newManufacturer}
              onChange={(e) => setNewManufacturer(e.target.value)}
              required
              sx={{ 
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2
                }
              }}
            />
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 2 }}>
            <Button 
              onClick={() => setAddDialogOpen(false)}
              sx={{ textTransform: 'none' }}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="contained"
              sx={{ 
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2,
                px: 3
              }}
            >
              Add Manufacturer
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Points of Contact Dialog */}
      <ManufacturerGotoPersons
        open={contactPersonsDialogOpen}
        onClose={closeContactPersonsDialog}
        manufacturer={selectedManufacturer}
        onContactChange={loadContactPersonsCounts}
      />
    </Box>
  );
}

export default Manufacturers;